import { type AxiosError } from 'axios'

import { isNonNullObject } from './utils/assert'
import { type SdkError, type SdkErrorType } from './error'
import { type OutputErrorMessage } from './generated'

const errorByType = (data?: OutputErrorMessage) => {
  const errorType = data?.type

  switch (errorType) {
    case '/reauthentication/failed':
    case '/authentication/multiple-accounts':
      return 'authentificationFailed'
    default:
      return 'functionalError'
  }
}

const isAxiosError = (error: unknown): error is AxiosError =>
  isNonNullObject(error) && 'isAxiosError' in error && error.isAxiosError === true

const isOutputErrorMessage = (data: NonNullable<object>): data is OutputErrorMessage =>
  'behaviour' in data && typeof data.behaviour === 'string'

const isAxiosErrorWithOutputErrorMessage = (axiosError: AxiosError): axiosError is AxiosError<OutputErrorMessage> =>
  isNonNullObject(axiosError.response?.data) && isOutputErrorMessage(axiosError.response.data)

const createSdkError = (error: unknown): SdkError => {
  if (!isAxiosError(error)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const displayedMessage = isNonNullObject(error) ? (error as any).response?.data?.details : undefined

    return {
      isSdkError: true,
      type: 'technicalError',
      displayedMessage,
    }
  }

  let errorType: SdkErrorType = 'serverError'
  const data = isAxiosErrorWithOutputErrorMessage(error) ? error.response?.data : undefined

  if (
    isAxiosErrorWithOutputErrorMessage(error) &&
    typeof error.response?.status === 'number' &&
    error.response.status < 500
  ) {
    errorType = errorByType(data)
  } else if (error.message === 'Network Error') {
    errorType = 'networkError'
  }

  return {
    isSdkError: true,
    type: errorType,
    data,
    displayedMessage: data?.details,
  }
}

type PromiseDescriptorValue<ResultType> = (...args: any[]) => Promise<ResultType>

/**
 * Error function Decorator Try/Catch
 *
 * @see https://www.typescriptlang.org/docs/handbook/decorators.html#method-decorators
 *
 * Usage: @Catch<ResultType>()
 *
 * Example: @Catch<LongItineraryProposals | undefined>()
 */
type CatchError = <ResultType>() => (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  target: any,
  propertyKey: string,
  descriptor: PropertyDescriptor,
) => TypedPropertyDescriptor<PromiseDescriptorValue<ResultType>>

export const Catch: CatchError = () => (_target, propertyKey, descriptor) => ({
  ...descriptor,
  value: async function DescriptorValue(...args: any[]) {
    try {
      return await Promise.resolve(descriptor.value.apply(this, args))
    } catch (error) {
      return Promise.reject(createSdkError(error))
    }
  },
})
